import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useId } from 'react';
import { Link } from 'react-router-dom';
import { receiptDocumentPath } from '../../../api/receipt.api';
import { CARD } from '../../../components/detail/detail-classes';
import { DownloadLabel } from '../../../components/detail/DownloadLabel';
import { TaxStatusTag } from '../../../components/donation/TaxStatusTag';
import { Button } from '../../../components/ui/button';
import {
  charityDonationsPath,
  DONATION_TONE
} from '../../../constants/donation.const';
import { useDownloadInFlight } from '../../../hooks/useDownloadInFlight';
import type {
  CharityYearSummary,
  DonationLine
} from '../../../types/donation.types';
import { nameInitials, pluralize } from '../../../utils/donation-format';
import { formatCurrency } from '../../../utils/fiat-currency.util';
import { DonationLineRow } from './DonationLineRow';

interface CharityRowProps {
  summary: CharityYearSummary;
  year: number;
  expanded: boolean;
  onToggle: () => void;
  onDownloadReceipt: (donation: DonationLine) => void;
  onDownloadAll: () => void;
}

/** One charity's year: its total, and when expanded its latest donations and receipts. */
export function CharityRow({
  summary,
  year,
  expanded,
  onToggle,
  onDownloadReceipt,
  onDownloadAll
}: CharityRowProps) {
  const { charity, currency, donation_count } = summary;
  const panelId = useId();
  const Chevron = expanded ? ChevronUpIcon : ChevronDownIcon;
  const bundlePreparing = useDownloadInFlight(
    receiptDocumentPath.charityBundle(charity.id, year)
  );

  return (
    <div className={CARD}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        className="flex w-full items-center gap-3 text-left"
      >
        <span
          className={clsx(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-sm font-semibold',
            DONATION_TONE.iconTile
          )}
          aria-hidden="true"
        >
          {nameInitials(charity.name)}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-sm font-semibold text-gray-900">
            {charity.name}
          </span>
          <TaxStatusTag taxDeductible={charity.tax_deductible} />
          <span className="text-xs text-gray-500">
            {pluralize(donation_count, 'donation', 'donations')}
            {charity.cause && ` · ${charity.cause}`}
          </span>
        </span>
        <span className="shrink-0 text-base font-bold text-gray-900">
          {formatCurrency(summary.total, currency)}
        </span>
        <Chevron
          className="h-4 w-4 shrink-0 text-gray-500"
          aria-hidden="true"
        />
      </button>

      {expanded && (
        <div id={panelId} className="mt-4 border-t border-gray-100">
          <ul className="divide-y divide-gray-100 border-b border-gray-100">
            {summary.latest.map((donation) => (
              <DonationLineRow
                key={donation.donation_ref}
                donation={donation}
                primary={`${dayjs(donation.date).format('DD MMM')} · ${donation.store_name}`}
                secondary={donation.donation_ref}
                onDownloadReceipt={onDownloadReceipt}
              />
            ))}
          </ul>
          <Link
            to={charityDonationsPath(charity.id, year)}
            className={clsx(
              'mt-3 inline-block text-sm font-semibold hover:underline',
              DONATION_TONE.text
            )}
          >
            View all {pluralize(donation_count, 'donation', 'donations')}
          </Link>
          <Button
            type="button"
            variant="outline"
            onClick={onDownloadAll}
            disabled={bundlePreparing}
            aria-busy={bundlePreparing}
            className={clsx(
              'mt-3 h-12 w-full rounded-xl text-sm font-semibold',
              DONATION_TONE.outlineButton
            )}
          >
            <DownloadLabel pending={bundlePreparing}>
              Download all {pluralize(donation_count, 'receipt', 'receipts')}{' '}
              (PDF)
            </DownloadLabel>
          </Button>
        </div>
      )}
    </div>
  );
}
